import {
  createTheme,
  Grid,
  Paper,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const defaultContext = {
  slideWidth: 1200,
  //slideHeight: 600,
};

const slidePalette = {
  green: "#148668",
  brightGreen: "#94dc5c",
  lightGreen: "#e3f8cf",
};

let slideThemeConfig = (theme) => {
  return {
    slidePalette,
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        color: theme.palette_custom.darkBlue,
        fontWeight: theme.fontWeight[400],
        fontSize: theme.fontSize.lg,
        background: null,
      },
      h2: {
        color: slidePalette.lightGreen,
        fontWeight: theme.fontWeight[400],
        fontSize: theme.fontSize.sm,
        backgroundColor: slidePalette.green,
        display: "block",
        padding: "0.1rem 0.5rem",
        textTransform: "uppercase",
      },
      h3: {
        fontSize: "0.8rem",
        fontWeight: theme.fontWeight[700],
        marginBottom: "0.4rem",
      },
      body2: {
        fontSize: "0.6rem",
        fontWeight: theme.fontWeight[400],
      },
    },
  };
};

const SlideDesignContext = createContext();

export const useSlideDesign = () => {
  const context = useContext(SlideDesignContext);
  if (!context) {
    throw new Error("useSlideDesign must be used within a SlideDesignProvider");
  }
  return context;
};

const SlideContent = ({ children, parentRef }) => {
  const { slideWidth, slideHeight } = useSlideDesign();
  const [calculatedScale, setCalculatedScale] = useState(1);
  const { scaleActive, loading } = useContext(SlideDesignContext);
  const ref = useRef();

  const calculateScale = () => {
    const width = ref.current ? ref.current.offsetWidth : 500;
    const height = ref.current ? ref.current.offsetHeight : 500;
    let parentWidth = 500;
    if (parentRef && parentRef.current) {
      parentWidth = parseFloat(parentRef.current.clientWidth); // Subtracting padding
    }
    const scale = Math.min(1, parentWidth / parseFloat(width));
    let parentHeight = slideHeight ? slideHeight : height;
    if (scaleActive) {
      parentHeight *= scale;
    }
    if (
      Math.abs(parseFloat(parentRef.current.offsetHeight) - parentHeight) >
      0.001
    ) {
      parentRef.current.style.height = `${parentHeight}px`;
    }
    setCalculatedScale(scale);
  };

  useEffect(() => {
    calculateScale();
    const a = setTimeout(calculateScale, 100); // Allow DOM to settle
    return () => {
      clearTimeout(a);
    };
  });

  useEffect(() => {
    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => {
      window.removeEventListener("resize", calculateScale);
    };
  }, []);

  const scale = scaleActive ? calculatedScale : 1;

  return (
    <Box
      ref={ref}
      sx={{
        padding: "4px",
        width: slideWidth + "px",
        height: slideHeight + "px",
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        marginX: "auto",
      }}
    >
      <Paper elevation={3}>
        <Grid container sx={{ padding: 2 }}>
          {children}
        </Grid>
      </Paper>
    </Box>
  );
};

export const SlideStyle = ({ children }) => {
  const theme = useTheme();
  const slideTheme = createTheme(theme, slideThemeConfig(theme));
  return <ThemeProvider theme={slideTheme}>{children}</ThemeProvider>;
};

const Slide = forwardRef(({ children, ...props }, ref) => {
  const context = useMemo(
    () => ({
      ...defaultContext,
      ...props,
    }),
    [props],
  );

  const innerRef = ref || useRef();
  let content = children;

  if (context.loading) {
    content = <Typography>No data provided</Typography>;
  }

  return (
    <SlideDesignContext.Provider value={context}>
      <SlideStyle>
        <Box
          sx={{
            overflowX: "scroll",
            marginX: "-4px !important",
            width: "calc(100% + 8px)",
          }}
        >
          <Box ref={innerRef}>
            <SlideContent parentRef={innerRef}>{content}</SlideContent>
          </Box>
        </Box>
      </SlideStyle>
    </SlideDesignContext.Provider>
  );
});

export default Slide;
